const DATE_TYPES = new Set([
  "date",
  "birthday",
]);

const DATETIME_TYPES = new Set([
  "datetime",
  "datetime_with_time",
  "time",
  "timestamp",
  "time-and-date",
  "time_and_date",
]);

const NUMBER_TYPES = new Set([
  "number",
  "integer",
  "bigint",
  "int",
  "double",
  "float",
  "decimal",
  "decimal-number",
  "decimal_number",
  "rating",
  "currency",
  "percent",
]);

const BOOLEAN_TYPES = new Set(["bool", "boolean", "booleancheckbox"]);

const MULTI_OPTION_TYPES = new Set([
  "listbox",
  "checkbox",
  "multipicklist",
  "multiselectpicklist",
  "multi-select",
  "multi_select",
]);

const SINGLE_OPTION_TYPES = new Set([
  "dropdown",
  "radio",
  "enumeration",
  "picklist",
  "single-select",
  "single_select",
  "status",
  "list",
]);

const TEXTAREA_TYPES = new Set(["textarea", "paragraph", "long-text", "long_text"]);

const UNSUPPORTED_TYPES = new Set([
  "file",
  "html",
  "personal_name",
  "location",
  "geo",
  "record_reference",
  "actor_reference",
  "interaction",
]);

export const isUnsupportedIntegrationProperty = (property) => {
  if (!property) return false;
  if (property.unsupported === true) return true;
  if (typeof property.type !== "string") return false;
  return UNSUPPORTED_TYPES.has(property.type.toLowerCase());
};

// Single classification path. Returns null when the property has no usable type
// signal — callers must treat null as "unconstrained".
export const categoryForIntegrationProperty = (property) => {
  if (!property) return null;
  if (isUnsupportedIntegrationProperty(property)) return null;
  if (typeof property.type !== "string" || property.type.length === 0) return null;

  const rawType = property.type.toLowerCase();
  const hasOptions = Array.isArray(property.options) && property.options.length > 0;

  // Providers that encode multi-ness via a separate flag (Attio's
  // is_multiselect) instead of a distinct type string.
  if (rawType === "select") {
    return property.is_multiselect ? "multi_option" : "single_option";
  }

  // Attio uses "checkbox" for boolean; AC/HubSpot use "checkbox" for a multi-select
  // option control. Disambiguate by the presence of an options list.
  if (rawType === "checkbox") {
    return hasOptions ? "multi_option" : "boolean";
  }

  if (DATE_TYPES.has(rawType)) return "date";
  if (DATETIME_TYPES.has(rawType)) return "datetime";
  if (NUMBER_TYPES.has(rawType)) return "number";
  if (BOOLEAN_TYPES.has(rawType)) return "boolean";
  if (MULTI_OPTION_TYPES.has(rawType)) return "multi_option";
  if (SINGLE_OPTION_TYPES.has(rawType)) return "single_option";
  if (TEXTAREA_TYPES.has(rawType)) return "textarea";

  // Known string-like fallback (text, string, phone, address, hidden, etc.)
  return "text";
};

// Per-category allowed input types and defaults.
// Survey never constrains (allowed: null); it shows a warning when the
// integration type is NOT free-text compatible (date/datetime/number/boolean).
// Form/Quiz only constrain categories with fixed value/format expectations
// (date/datetime/number and single/multi option, plus boolean which has fixed
// true/false values). text/textarea accept any CF field type (allowed: null)
// since everything is sent to the integration as raw text.
const FORM_RULES = {
  date:          { allowed: ["date"],                                                            default: "date" },
  datetime:      { allowed: ["date"],                                                            default: "date" },
  number:        { allowed: ["number", "range"],                                                 default: "number" },
  boolean:       { allowed: ["dropdown", "select"],                                              default: "dropdown" },
  single_option: { allowed: ["dropdown", "select"],                                              default: "dropdown" },
  multi_option:  { allowed: ["select"],                                                          default: "select" },
  textarea:      { allowed: null,                                                                default: "paragraph" },
  text:          { allowed: null,                                                                default: "text" },
};

const QUIZ_RULES = {
  date:          { allowed: ["date"],                                                            default: "date" },
  datetime:      { allowed: ["date"],                                                            default: "date" },
  number:        { allowed: ["number", "range"],                                                 default: "number" },
  boolean:       { allowed: ["radio_survey", "radio", "dropdown"],                                               default: "radio_survey" },
  single_option: { allowed: ["radio_survey", "radio", "dropdown", "image", "color", "emoji", "icon", "letter"], default: "radio_survey" },
  multi_option:  { allowed: ["radio_survey", "checkbox", "survey", "image", "color", "emoji", "icon", "letter"], default: "radio_survey" },
  textarea:      { allowed: null,                                                                default: "paragraph" },
  text:          { allowed: null,                                                                default: "text" },
};

const SURVEY_WARN_DEFAULTS = {
  date: "date", datetime: "date", number: "number", boolean: "dropdown",
};

const RULES_BY_ELEMENT = { form: FORM_RULES, quiz: QUIZ_RULES };

export const allowedCfFieldTypesForCategory = (category, elementType = "form") => {
  if (!category) return null;
  if (elementType === "survey") {
    const warnDefault = SURVEY_WARN_DEFAULTS[category] || null;
    return { allowed: null, default: warnDefault };
  }
  const table = RULES_BY_ELEMENT[elementType] || FORM_RULES;
  return table[category] || null;
};

export const allowedCfFieldTypesForIntegrationProperty = (property, elementType = "form") => {
  const category = categoryForIntegrationProperty(property);
  return allowedCfFieldTypesForCategory(category, elementType);
};

// Convenience: locate the integration property hash for a given field, given
// website.integrations and the field's slug + service.
export const findIntegrationProperty = (website, field) => {
  if (!website || !field || !field.slug || !field.service || field.service === "ConvertFlow") {
    return null;
  }
  const integration = findIntegrationByService(website, field.service);
  if (!integration) return null;
  const props = integration.api_data && (integration.api_data.properties || integration.api_data.fields);
  if (!props) return null;
  return Object.values(props).find((p) => String(p.id) === String(field.slug)) || null;
};

const MERGE_TAG_PATTERN = /^\s*\{\{/;

const NATIVE_INPUT_FOR_CATEGORY = {
  date: "date",
  datetime: "datetime-local",
  number: "number",
  text: "text",
  textarea: "text",
};

const BOOLEAN_SELECT_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

// Resolve the input shape to render for a setting whose type is derived from
// the parent integration property. Returns null when no parent property is
// resolvable (callers should fall back to the configured input_type).
export const buildTypedFromParent = (property, settingValue) => {
  if (!property) return null;
  if (typeof settingValue === "string" && MERGE_TAG_PATTERN.test(settingValue)) {
    return { kind: "native", inputType: "text" };
  }
  const category = categoryForIntegrationProperty(property);
  const options = Array.isArray(property.options) ? property.options : [];

  if (category === "boolean") {
    return { kind: "select", options: BOOLEAN_SELECT_OPTIONS };
  }

  if (category === "multi_option" || (category === "single_option" && options.length > 0)) {
    return {
      kind: category === "multi_option" ? "multi_select" : "select",
      options: options.map((o) => ({ value: o.value, label: o.label || o.value })),
    };
  }

  return { kind: "native", inputType: NATIVE_INPUT_FOR_CATEGORY[category] || "text" };
};

export const findIntegrationByService = (website, service) => {
  if (!website || !service) return null;
  const integrations = website.integrations || {};
  return Object.values(integrations).find((i) => i && i.integration_type === service) || null;
};

export default allowedCfFieldTypesForIntegrationProperty;
